import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TopBar } from "../components/TopBar";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Bell, ArrowLeft, RotateCcw, Mail } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function NotificationPrefsPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [evRes, prefRes] = await Promise.all([
        api.get("/notifications/events"),
        api.get("/users/me/notification-prefs"),
      ]);
      setEvents(evRes.data || []);
      setPrefs(prefRes.data.prefs || {});
      setDefaults(prefRes.data.defaults || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/notification-prefs", { prefs });
      toast.success("Notification preferences saved");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    const next = {};
    events.forEach((e) => { next[e.key] = !!defaults[e.key]; });
    setPrefs(next);
    setDirty(true);
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <TopBar />
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
        <Link to="/dashboard" className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1.5 mb-4" data-testid="prefs-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
        </Link>

        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-zinc-950 flex items-center gap-3">
              <Bell className="h-7 w-7 text-zinc-400" />
              My Notifications
            </h1>
            <p className="text-sm text-zinc-600 mt-1">
              Pick which events you want emailed to <span className="font-mono text-zinc-900">{user?.email || "your inbox"}</span>.
              Mentions are always sent.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={resetToDefaults}
            className="border-zinc-200"
            data-testid="prefs-reset-defaults"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            Use {user?.role || "role"} defaults
          </Button>
        </div>

        <div className="bg-white border border-zinc-200 rounded-md shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
            <Mail className="h-4 w-4 text-zinc-500" />
            <h3 className="font-heading font-bold text-sm tracking-tight text-zinc-950">Email notifications</h3>
          </div>
          {loading && (
            <div className="p-6 text-sm text-zinc-400">Loading preferences…</div>
          )}
          {!loading && (
            <ul className="divide-y divide-zinc-100">
              {events.map((ev) => {
                const checked = !!prefs[ev.key];
                const isMention = ev.key === "mentioned";
                return (
                  <li key={ev.key} className="px-5 py-4 flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-zinc-950 flex items-center gap-2">
                        {ev.label}
                        {isMention && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 bg-zinc-100 rounded px-1.5 py-0.5">
                            Always on
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-500 mt-0.5">{ev.description}</p>
                    </div>
                    <Switch
                      checked={isMention ? true : checked}
                      onCheckedChange={(v) => !isMention && toggle(ev.key, v)}
                      disabled={isMention}
                      data-testid={`pref-${ev.key}`}
                    />
                  </li>
                );
              })}
            </ul>
          )}
          <div className="px-5 py-3 border-t border-zinc-100 flex items-center justify-end gap-2 bg-zinc-50">
            <Button
              size="sm"
              onClick={save}
              disabled={!dirty || saving}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="prefs-save"
            >
              {saving ? "Saving…" : "Save preferences"}
            </Button>
          </div>
        </div>

        <p className="text-[11px] text-zinc-400 mt-3 text-right">
          Defaults for <strong>{user?.role}</strong> are used when no preference is saved.
        </p>
      </main>
    </div>
  );
}
